import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaArrowLeft, FaChevronDown, FaChevronRight } from "react-icons/fa";
import RichText from "./RichText";
import { apiUrl } from "../apiConfig";

const API_BASE = `${apiUrl("therapist_api")}/`;

export default function HistoricalPathway() {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlClientId = urlParams.get("client_id");
  const urlGroupName = urlParams.get("group_name");

  const token = localStorage.getItem("token");

  const [clientId, setClientId] = useState(urlClientId || null);
  const [clientName, setClientName] = useState("");
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(urlGroupName || "");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSession, setOpenSession] = useState(null);

  // -- Resolve client_id from URL or localStorage ----------------------------
  useEffect(() => {
    if (urlClientId) {
      setClientId(urlClientId);
      return;
    }
    const savedState = localStorage.getItem("assessmentState");
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.currentSessionId && !clientId) {
          // fall through ? client_id is required, so skip here
        }
      } catch (e) {
        console.error("Error parsing saved state", e);
      }
    }
  }, [urlClientId, clientId]);

  const fetchGroupList = async (cid) => {
    if (!cid || !token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}get_historical_pathway_data.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(cid) }),
      });
      const json = await response.json();
      if (json.status) {
        setClientName(json.client_name || "");
        setGroups(Array.isArray(json.groups) ? json.groups : []);
        setData(Array.isArray(json.data) ? json.data : []);
      } else {
        setError(json.message || "Failed to load historical data");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupList(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const fetchGroupData = async (groupName) => {
    if (!clientId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}get_historical_pathway_data.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), group_name: groupName }),
      });
      const json = await response.json();
      if (json.status) {
        setData(Array.isArray(json.data) ? json.data : []);
        setActiveGroup(groupName);
        setOpenSession(null);
      } else {
        setError(json.message || "Failed to load group data");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (groupName) => {
    if (groupName === activeGroup) return;
    fetchGroupData(groupName);
  };

  // Label each session as Initial / Review 1 / Review 2 ...
  const sessionLabels = useMemo(() => {
    const labelMap = {};
    const chronological = [...data].sort((a, b) => new Date(a.assessment_date || 0) - new Date(b.assessment_date || 0));
    let reviewCount = 0;
    chronological.forEach((s) => {
      const type = (s.appointment_type || "initial").toLowerCase();
      if (type === "review") {
        reviewCount += 1;
        labelMap[s.session_id] = `Review ${reviewCount}`;
      } else {
        labelMap[s.session_id] = "Initial";
      }
    });
    return data.map((s) => labelMap[s.session_id] || "Unknown");
  }, [data]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = d.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Render a single answer (string | array | HINE/HAI object)
  const renderAnswer = (ans) => {
    if (ans === null || ans === undefined || ans === "") {
      return <em style={{ color: "var(--cpa-gray-2, #626A6F)" }}>No answer</em>;
    }
    if (Array.isArray(ans)) {
      return ans.length > 0
        ? ans.map((item, i) => (
            <span key={i}>
              {i > 0 ? ", " : ""}
              <RichText html={item} />
            </span>
          ))
        : <em style={{ color: "var(--cpa-gray-2, #626A6F)" }}>No answer</em>;
    }
    if (typeof ans === "object") {
      if (ans.entries && ans.entries.length > 0) {
        return (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "13px" }}>
              <thead>
                <tr>
                  {Object.keys(ans.entries[0]).map((k) => (
                    <th key={k} scope="col" style={{ border: "1px solid #ddd", padding: "4px 8px", background: "#f5f5f5", textTransform: "capitalize" }}>
                      {k.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ans.entries.map((entry, i) => (
                  <tr key={i}>
                    {Object.values(entry).map((v, j) => (
                      <td key={j} style={{ border: "1px solid #ddd", padding: "4px 8px" }}>
                        {v === null || v === undefined || v === "" ? "-" : <RichText html={String(v)} />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      const value = ans.value;
      if (value !== null && value !== undefined && value !== "") {
        return <RichText html={String(value)} />;
      }
      return <em style={{ color: "var(--cpa-gray-2, #626A6F)" }}>No answer</em>;
    }
    return <RichText html={String(ans)} />;
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1100px", margin: "0 auto", fontFamily: "inherit" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
         <Link  onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
              <FaArrowLeft className="back-icon" aria-hidden="true" />
              Previous page
            </Link>
        <h1 style={{ margin: 0, color: "#2f7a3d", fontSize: "22px" }}>
          Historical Pathway Data {clientName ? `for ${clientName}` : ""}
        </h1>
      </div>

      {/* Group Tabs */}
      <div style={{ marginBottom: "16px" }}>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "6px", color: "#333" }}>
          Pathway Group Name
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {groups.length > 0 ? (
            groups.map((g) => (
              <button
                key={g}
                onClick={() => handleGroupClick(g)}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: activeGroup === g ? "2px solid #2f7a3d" : "1px solid var(--cpa-gray-3, #737A7E)",
                  background: activeGroup === g ? "#2f7a3d" : "#fff",
                  color: activeGroup === g ? "#fff" : "#333",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {g}
              </button>
            ))
          ) : (
            !loading && <span style={{ color: "var(--cpa-gray-2, #626A6F)" }}>No pathway groups available.</span>
          )}
        </div>
      </div>

      {loading && <div style={{ padding: "20px", textAlign: "center", color: "#666" }} role="status" aria-live="polite">Loading...</div>}
      {error && <div style={{ padding: "20px", textAlign: "center", color: "#c00" }} role="alert">{error}</div>}

      {!loading && !error && data.length === 0 && (
        <div style={{ padding: "20px", textAlign: "center", color: "var(--cpa-gray-2, #626A6F)" }}>
          No historical assessment data found.
        </div>
      )}

      {/* Sessions Accordion */}
      {!loading && !error && data.length > 0 && (
        <div>
          {data.map((session, idx) => {
            const isOpen = openSession === idx;
            return (
              <div key={session.session_id} style={{ border: "1px solid #ddd", borderRadius: "8px", marginBottom: "10px", overflow: "hidden" }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenSession(isOpen ? null : idx)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenSession(isOpen ? null : idx); } }}
                  aria-expanded={isOpen}
                  aria-controls={`session-panel-${session.session_id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    background: "#f7f7f7",
                    cursor: "pointer",
                    fontWeight: "600",
                    color: "#222",
                  }}
                >
                  <span>
                    Historical Session {idx + 1} ({sessionLabels[idx] || "Unknown"})
                    <span style={{ fontWeight: "normal", color: "var(--cpa-gray-2, #626A6F)", marginLeft: "10px", fontSize: "13px" }}>
                      {formatDate(session.assessment_date)}
                    </span>
                  </span>
                  {isOpen ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
                </div>

                {isOpen && (
                  <div id={`session-panel-${session.session_id}`} role="region" style={{ padding: "8px 16px 16px" }}>
                    {session.pathway && session.pathway.length > 0 ? (
                      session.pathway.map((q, qi) => (
                        <div key={qi} style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}>
                          <div style={{ fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                            <RichText html={q.question} />
                          </div>
                          <div style={{ color: "#555" }}>{renderAnswer(q.answer)}</div>
                          {q.options && q.options.length > 0 && (
                            <div style={{ marginTop: "6px", paddingLeft: "14px", color: "#666", fontSize: "13px" }}>
                              {q.options
                                .filter((o) => o.selected)
                                .map((o, oi) => (
                                  <div key={oi}><RichText html={o.label} />{o.answer && o.answer !== o.label ? ` - ${renderAnswer(o.answer)}` : ""}</div>
                                ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: "var(--cpa-gray-2, #626A6F)" }}>No questions answered for this session.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

